import isNull from 'lodash-es/isNull';
import { fromEvent, merge, Observable } from 'rxjs';
import { DEFAULT_TEXT_UNDER_CURSOR, TextUnderCursor } from '../../helpers/textUnderCursor';
import { createConnectingService } from '../base/connectingService';
import { ElementService } from '../dom/elementService';
import { NULL_TEXT_UNDER_CURSOR_SERVICE, TextUnderCursorService } from './textUnderCursorService';

type InputElement = HTMLTextAreaElement | HTMLInputElement;

export function createInputTextUnderCursorService(
  element$: Observable<InputElement | null>,
): TextUnderCursorService {
  return createConnectingService(
    element$,
    () => NULL_TEXT_UNDER_CURSOR_SERVICE,
    element => new InputTextUnderCursorService(element),
  );
}

export class InputTextUnderCursorService
  extends ElementService<InputElement>
  implements TextUnderCursorService {
  constructor(element: InputElement) {
    super(element, {
      changes$: merge(
        // Any of these things can change the selection.
        // TODO: Can we narrow this down and de-dupe this a bit?
        fromEvent(element, 'input'),
        fromEvent(element, 'select'),
        fromEvent(element, 'keydown'),
        fromEvent(element, 'keyup'),
        fromEvent(element, 'mousedown'),
        fromEvent(element, 'mouseup'),
      ),
    });
  }

  get isActive() {
    return Boolean(this.element.ownerDocument && this.element.ownerDocument.activeElement === this.element);
  }

  get textUnderCursor() {
    const { value, selectionStart, selectionEnd, selectionDirection } = this.element;
    if (isNull(selectionStart) || isNull(selectionEnd)) {
      return DEFAULT_TEXT_UNDER_CURSOR;
    }

    return {
      // TODO: Only return the current line of text for textareas.
      text: value,
      anchorOffset: selectionDirection === 'backward' ? selectionEnd : selectionStart,
      focusOffset: selectionDirection === 'backward' ? selectionStart : selectionEnd,
    };
  }

  setTextUnderCursor(textUnderCursor: TextUnderCursor | undefined) {
    if (!textUnderCursor) {
      return;
    }

    // eslint-disable-next-line no-param-reassign
    const { element, document } = this;
    element.value = textUnderCursor.text;

    if (textUnderCursor.anchorOffset <= textUnderCursor.focusOffset) {
      element.setSelectionRange(textUnderCursor.anchorOffset, textUnderCursor.focusOffset);
    } else {
      element.setSelectionRange(textUnderCursor.focusOffset, textUnderCursor.anchorOffset, 'backward');
    }

    // Dispatch a synthetic input event to propagate the change.
    const event = document.createEvent('Event');
    event.initEvent('input', true, false);
    element.dispatchEvent(event);
  }

  getBoundingClientRect() {
    return this.element.getBoundingClientRect();
  }
}
